
import { logger } from "../logger/Logger";
import * as et from "../application/EventTypes";
import { SYNTHETIC_ROOT_ID } from "../globals";
import { VisibilityManager } from "./VisibilityManager";

/** @import {Viewer3DImpl} from "../application/Viewer3DImpl" */
/** @import {Model} from "../application/Model" */
/** @import {InstanceTree} from "../wgs/scene/InstanceTree" */

export class MultiModelVisibilityManager {

  /** @type {Viewer3DImpl} */
  viewerImpl;

  /** @type {Model[]} */
  models = [];

  /**
   * @param {Viewer3DImpl} viewerImpl
   */
  constructor(viewerImpl) {
    this.viewerImpl = viewerImpl;
  }

  /**
   * @param {Model} model
   */
  addModel(model) {
    if (this.models.indexOf(model) === -1) {
      model.visibilityManager = new VisibilityManager(this.viewerImpl, model);
      this.models.push(model);
    }
  }

  /**
   * @param {Model} model
   */
  removeModel(model) {
    const idx = this.models.indexOf(model);

    if (idx === -1) return;

    // clear visibility states (revert all ghosting)
    model.visibilityManager.setAllVisibility(true);

    model.visibilityManager = null;
    this.models.splice(idx, 1);
  }

  warn() {
    if (this.models.length > 1) {
      logger.warn("This selection call does not yet support multiple models.");
    }
  }

  /**
   * Get a list of all dbIds that are currently isolated, grouped by model.
   *
   * @returns {{ model: Model, ids: number[] }[]} Containing objects with `{ model: <instance>, ids: Number[] }`.
   * @private
   */
  getAggregateIsolatedNodes() {
    /** @type {{ model: Model, ids: number[] }[]} */
    const res = [];
    for (const model of this.models) {
      const ids = model.visibilityManager.getIsolatedNodes();
      if (ids.length) res.push({ model, ids });
    }
    return res;
  }

  /**
   * @param {Model} model
   * @returns {number[]}
   */
  getIsolatedNodes(model) {
    if (!model) {
      this.warn();
      model = this.models[0];
    }
    return model.visibilityManager.getIsolatedNodes();
  }

  /**
   * @returns {{ model: Model, ids: number[] }[]}
   */
  getAggregateHiddenNodes() {
    /** @type {{ model: Model, ids: number[] }[]} */
    const res = [];
    for (const model of this.models) {
      const ids = model.visibilityManager.getHiddenNodes();
      if (ids.length) res.push({ model, ids });
    }
    return res;
  }

  /**
   * @param {Model} model
   * @returns {number[]}
   */
  getHiddenNodes(model) {
    if (!model) {
      this.warn();
      model = this.models[0];
    }
    return model.visibilityManager.getHiddenNodes();
  }

  /**
   * @param {Model} model
   * @param {number} dbId
   * @returns {boolean}
   */
  isNodeVisible(model, dbId) {
    if (!model) {
      this.warn();
      model = this.models[0];
    }
    return model.visibilityManager.isNodeVisible(dbId);
  }

  /**
   * @param {number|number[]} node
   * @param {Model} model
   */
  isolate(node, model) {
    if (!model) {
      this.warn();
      model = this.models[0];
    }

    // unlike aggregateIsolate, calling isolate only touches the passed model
    this.aggregateIsolate([{ model, ids: node }], { hideLoadedModels: false });
  }

  /**
   * Isolate nodes (dbids) associated with a model.
   * @param {object[]} isolation - an array of isolation objects
   * @param {Model} isolation.model - Model
   * @param {number|number[]} isolation.ids - dbids to isolate
   * @param {object} [options] - custom options
   * @param {boolean} [options.hideLoadedModels=true] - if set to `false`, the visibility of other loaded models will not change (`true` by default)
   */
  aggregateIsolate(isolation, options) {
    if (!isolation || isolation.length === 0) {// all visible
      for (const model of this.models) {
        model.visibilityManager.setAllVisibility(true);
      }
    } else {// Something's isolated
      const hideOthers = options?.hideLoadedModels ?? true;

      for (const model of this.models) {

        const item = isolation.find((i) => i.model == model);

        if (item) {
          const ids = item.ids || item.selection;

          model.visibilityManager.isolate(ids);
        } else if (hideOthers) {
          model.visibilityManager.setAllVisibility(false);
        }
      }
    }
    this.#fireAggregateIsolationChangedEvent();
  }

  /**
   * Makes the children of a given node visible and everything else not visible
   *
   * @param {number} node
   * @param {Model} model
   */
  hide(node, model) {
    if (!model) {
      this.warn();
      model = this.models[0];
    }
    model.visibilityManager.hide(node);
  }

  /**
   * Hide the dbids associated in a model
   *
   * Fires the Autodesk.Viewing.AGGREGATE_HIDDEN_CHANGED_EVENT.
   *
   * @param {{ model: Model, ids: number[] }[]} nodes
   */
  aggregateHide(nodes) {
    if (!nodes || nodes.length === 0) return;
    for (const node of nodes) {
      const model = node.model;
      const ids = node.ids || node.selection;
      // Fire the av.HIDE_EVENT only if there is one entry.
      model.visibilityManager.hide(ids, this.models.length === 1);
    }

    const hidden = this.getAggregateHiddenNodes();
    this.viewerImpl.api.dispatchEvent({ type: et.AGGREGATE_HIDDEN_CHANGED_EVENT, hidden });
  }

  /**
   * Restore states of nodes (dbIds) associated with a model
   *
   * A state may have both isolated and hidden nodes.
   *
   * @param {object[]} isolateAndHide - the array of states
   * @param {Model} isolateAndHide.model - the model
   * @param {number[]} isolateAndHide.isolatedIds - the array of dbIds to isolate
   * @param {number[]} isolateAndHide.hiddenIds - the array of dbIds to hide
   */
  aggregateRestore(isolateAndHide) {
    if (!isolateAndHide || isolateAndHide.length === 0) {
      return;
    }
    for (const model of this.models) {
      const state = isolateAndHide.find((i) => i.model === model);
      if (!state) continue;
      const { isolatedIds, hiddenIds } = state;
      model.visibilityManager.isolate(isolatedIds);
      // Fire the av.HIDE_EVENT only if there is one entry.
      model.visibilityManager.hide(hiddenIds, this.models.length === 1, true);
    }
    this.#fireAggregateIsolationChangedEvent();
    const hidden = this.getAggregateHiddenNodes();
    this.viewerImpl.api.dispatchEvent({ type: et.AGGREGATE_HIDDEN_CHANGED_EVENT, hidden });
  }

  /**
   * Inverse of `hide`
   *
   * @param {number} node
   * @param {Model} model
   */
  show(node, model) {
    if (!model) {
      this.warn();
      model = this.models[0];
    }
    model.visibilityManager.show(node);
  }

  /**
   * Toggle the visibility of the node given
   *
   * @param {number} node
   * @param {Model} model
   */
  toggleVisibility(node, model) {
    if (!model) {
      this.warn();
      model = this.models[0];
    }
    model.visibilityManager.toggleVisibility(node);
  }

  /**
   * @param {number} node
   * @param {boolean} visible
   * @param {Model} model
   * @param {boolean} [skipEventing=false] - optional flag to skip the resulting event, defaults to false
   * @param {boolean} [setRecursive=true] - optional flag to set the visibility recursively, defaults to true
   */
  setVisibilityOnNode(node, visible, model, skipEventing = false, setRecursive = true) {
    if (!model) {
      this.warn();
      model = this.models[0];
    }
    model.visibilityManager.setVisibilityOnNode(node, visible, skipEventing, setRecursive);
  }

  /**
   * @param {number} node
   * @param {Model} model
   */
  toggleVisibleLocked(node, model) {
    if (!model) {
      this.warn();
      model = this.models[0];
    }
    model.visibilityManager.toggleVisibleLocked(node);
  }

  /**
   * @param {number} node
   * @param {boolean} locked
   * @param {Model} model
   */
  lockNodeVisible(node, locked, model) {
    if (!model) {
      this.warn();
      model = this.models[0];
    }
    model.visibilityManager.lockNodeVisible(node, locked);
  }

  /**
   * @param {number} node
   * @param {Model} model
   * @returns {boolean}
   */
  isNodeVisibleLocked(node, model) {
    if (!model) {
      this.warn();
      model = this.models[0];
    }
    return model.visibilityManager.isNodeVisibleLocked(node, model);
  }

  /**
   * @param {number} node
   * @param {boolean} isOff
   * @param {Model} model
   * @param {number[]} nodeChildren
   * @param {number[]} nodeFragments
   */
  setNodeOff(node, isOff, model, nodeChildren, nodeFragments) {
    if (!model) {
      this.warn();
      model = this.models[0];
    }
    model.visibilityManager.setNodeOff(node, isOff, nodeChildren, nodeFragments);
  }

  /**
   * @private
   */
  #fireAggregateIsolationChangedEvent() {

    const isolation = this.getAggregateIsolatedNodes();

    // Legacy event
    if (this.models.length === 1) {
      this.viewerImpl.api.dispatchEvent({
        type: et.ISOLATE_EVENT,
        nodeIdArray: isolation.length ? isolation[0].ids : [],
        model: this.models[0]
      });
    }

    // Always fire
    this.viewerImpl.api.dispatchEvent({ type: et.AGGREGATE_ISOLATION_CHANGED_EVENT, isolation });
  }
}